import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, updateDoc, doc, getDoc, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { FaGlassWhiskey, FaSignOutAlt, FaClock, FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaSpinner, FaSearch, FaTimes, FaUtensils, FaExclamationTriangle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import ViewMenuServer from '../server/ViewMenuServer';
import './bardashboard.css';

const BarInterface = () => {
  const [acceptedItems, setAcceptedItems] = useState([]);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [serverFirstName, setServerFirstName] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [totalHours, setTotalHours] = useState(0);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingDrinks, setLoadingDrinks] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDrinks, setFilteredDrinks] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});
  const menuId = "ZifkqWN99oRgRNM0Nisz";

  // Fonction pour basculer l'expansion d'une carte
  const toggleCardExpansion = (itemId) => {
    setExpandedCards(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  useEffect(() => {
    const fetchUserRole = async (currentUser) => {
      try {
        setLoading(true);
        const userRef = doc(db, 'identifications', currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          if (userData.role !== 'bar') {
            setIsAuthorized(false);
            setTimeout(() => navigate('/login'), 3000);
          } else {
            setServerFirstName(userData.firstName);
          }
        } else {
          setIsAuthorized(false);
          setTimeout(() => navigate('/login'), 3000);
        }
      } catch (error) {
        setIsAuthorized(false);
        setTimeout(() => navigate('/login'), 3000);
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        fetchUserRole(currentUser);
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const fetchSchedule = async () => {
      if (user) {
        try {
          const scheduleRef = collection(db, 'horaires');
          const q = query(scheduleRef, where('employeeId', '==', user.uid));
          const querySnapshot = await getDocs(q);

          querySnapshot.forEach((doc) => {
            setSchedule(doc.data().schedule);
          });
        } catch (error) {
          // Erreur silencieuse
        }
      }
    };

    fetchSchedule();
  }, [user]);

  useEffect(() => {
    const fetchCommandItems = async () => {
      try {
        setLoadingDrinks(true);
        const clientsCollection = collection(db, 'clients');
        const unsubscribers = [];

        const unsubscribe = onSnapshot(clientsCollection, (clientsSnapshot) => {
          let allDrinks = [];
          let processedClients = 0;
          const totalClients = clientsSnapshot.size;

          if (totalClients === 0) {
            setAcceptedItems([]);
            setFilteredDrinks([]);
            setLoadingDrinks(false);
            return;
          }

          clientsSnapshot.forEach((clientDoc) => {
            const clientId = clientDoc.id;
            const commandesCollection = collection(db, `clients/${clientId}/commande`);
            const qAccepted = query(commandesCollection, where('statut', '==', 'Accepté'));

            const commandeUnsubscribe = onSnapshot(qAccepted, (commandesSnapshot) => {
              let clientDrinks = [];
              
              commandesSnapshot.forEach((commandeDoc) => {
                const commandeData = commandeDoc.data();
                if (commandeData.items && Array.isArray(commandeData.items)) {
                  const commandeItems = commandeData.items
                    .filter(item => item.categorie === 'boissons')
                    .map((item) => ({
                      ...item,
                      clientId,
                      commandeId: commandeDoc.id,
                      table: commandeData.table,
                      prenom: commandeData.prenom,
                      email: commandeData.email,
                    }));
                  clientDrinks = [...clientDrinks, ...commandeItems];
                }
              });

              allDrinks = allDrinks.filter(drink => drink.clientId !== clientId);
              allDrinks = [...allDrinks, ...clientDrinks];

              setAcceptedItems([...allDrinks]);
              setFilteredDrinks([...allDrinks]);
              
              processedClients++;
              if (processedClients === 1) {
                setLoadingDrinks(false);
              }
            });

            unsubscribers.push(commandeUnsubscribe);
          });
        });

        unsubscribers.push(unsubscribe);

        return () => {
          unsubscribers.forEach(unsub => unsub());
        };
      } catch (error) {
        setLoadingDrinks(false);
      }
    };

    fetchCommandItems();
  }, []);

  const calculateTotalHours = useCallback(async () => {
    if (!schedule || !user) return;
  
    const currentDate = new Date();
    const currentDay = currentDate.getDate(); // Jour actuel du mois
  
    const daysInWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const hoursWorked = [];
  
    // On va vérifier chaque jour de la semaine jusqu'au jour actuel du mois
    for (let i = 0; i < currentDay; i++) {
      const dayOfWeek = daysInWeek[i % 7]; // Calculer le jour de la semaine pour la date
      const daySchedule = schedule[dayOfWeek];
  
      if (daySchedule && daySchedule !== "Ne travaille pas") {
        const periods = daySchedule.split(" / "); // Diviser les périodes par "/"
        let dailyHours = 0;
      
        periods.forEach((period) => {
          const [start, end] = period.split(" - ");
          const startTime = new Date(`2023-12-01T${start}:00`);
          const endTime = new Date(`2023-12-01T${end}:00`);
          const hours = (endTime - startTime) / 1000 / 60 / 60; // Conversion en heures
          dailyHours += hours;
        });
      
        hoursWorked.push(dailyHours); // Ajouter les heures du jour au tableau
      }      
    }
  
    let total = hoursWorked.reduce((acc, hours) => acc + hours, 0); // Calcul du total
  
    // Ajouter les heures supplémentaires depuis la base de données
    try {
      const scheduleRef = collection(db, 'horaires');
      const q = query(scheduleRef, where('employeeId', '==', user.uid));
      const querySnapshot = await getDocs(q);
  
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.heure_supp) {
          total += parseFloat(data.heure_supp);
        }
  
        updateDoc(doc.ref, { total_hours: total });
      });

      setTotalHours(total);
    } catch (error) {
      // Erreur silencieuse
    }
  }, [schedule, user]);

  useEffect(() => {
    calculateTotalHours(); // Appeler le calcul des heures dès que l'emploi du temps est récupéré
  }, [schedule, calculateTotalHours]);

  const toggleDrinkStatus = async (clientId, commandeId, dishName, newStatus) => {
    try {
      const commandeRef = doc(db, `clients/${clientId}/commande`, commandeId);
      const commandeSnap = await getDoc(commandeRef);
  
      if (commandeSnap.exists()) {
        const commandeData = commandeSnap.data();
  
        const updatedItems = commandeData.items.map((item) =>
          item.nom === dishName
            ? { ...item, drink_statut: newStatus }
            : item
        );
  
        await updateDoc(commandeRef, { items: updatedItems });
  
        setAcceptedItems((prevItems) =>
          prevItems.map((item) =>
            item.clientId === clientId && item.commandeId === commandeId && item.nom === dishName
              ? { ...item, drink_statut: newStatus }
              : item
          )
        );
        setFilteredDrinks((prevItems) =>
          prevItems.map((item) =>
            item.clientId === clientId && item.commandeId === commandeId && item.nom === dishName
              ? { ...item, drink_statut: newStatus }
              : item
          )
        );
      }
    } catch (error) {
      // Erreur silencieuse
    }
  };

  // Fonction de recherche
  useEffect(() => {
    if (searchTerm) {
      const filtered = acceptedItems.filter(item =>
        item.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.table.toString().includes(searchTerm) ||
        item.prenom.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredDrinks(filtered);
    } else {
      setFilteredDrinks(acceptedItems);
    }
  }, [searchTerm, acceptedItems]);

  const formatHours = (hours) => {
    const fullHours = Math.floor(hours); // Partie entière des heures
    const minutes = Math.round((hours - fullHours) * 60); // Partie décimale convertie en minutes
    return `${fullHours}h${minutes.toString().padStart(2, '0')}`; // Format h:mm avec zéro-padding
  };

  // Afficher l'emploi du temps sous forme de tableau
  const renderSchedule = () => {
    if (!schedule) return <p>Chargement de l'emploi du temps...</p>;
  
    const daysInFrench = {
      Sunday: "Dimanche",
      Monday: "Lundi",
      Tuesday: "Mardi",
      Wednesday: "Mercredi",
      Thursday: "Jeudi",
      Friday: "Vendredi",
      Saturday: "Samedi"
    };
  
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
    return (
      <table className="bar-schedule-table">
        <thead>
          <tr>
            {dayNames.map((day) => (
              <th key={day}>{daysInFrench[day]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {dayNames.map((day) => (
              <td key={day}>
                {schedule[day] === 'Ne travaille pas' ? 'Ne travaille pas' : schedule[day]}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    );
  };

  if (!isAuthorized) {
    return (
      <div className="bar-dash-unauthorized-container">
        <div className="bar-dash-unauthorized-popup">
          <FaExclamationTriangle className="bar-dash-unauthorized-icon" />
          <h2>Accès Refusé</h2>
          <p>Vous n'êtes pas autorisé à accéder à cette page.</p>
          <p className="bar-dash-unauthorized-redirect">Redirection en cours...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bar-dash-loading-container">
        <FaSpinner className="bar-dash-loading-spinner" />
        <p className="bar-dash-loading-text">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="bar-dash-container">
      {/* Header */}
      <div className="bar-dash-header">
        <div className="bar-dash-header-content">
          <div className="bar-dash-title-section">
            <FaGlassWhiskey className="bar-dash-title-icon" />
            <div className="bar-dash-title-text">
              <h1 className="bar-dash-title">Interface Barman</h1>
              {serverFirstName && (
                <p className="bar-dash-subtitle">Bienvenue, {serverFirstName} !</p>
              )}
            </div>
          </div>
          <button 
            className="bar-dash-logout-btn"
            onClick={() => signOut(auth).then(() => navigate('/login'))}
          >
            <FaSignOutAlt />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="bar-dash-quick-actions">
        <button 
          className="bar-dash-action-btn bar-dash-schedule-btn"
          onClick={() => setShowSchedule(true)}
        >
          <FaClock className="bar-dash-action-icon" />
          <div className="bar-dash-action-text">
            <span className="bar-dash-action-title">Mes Heures</span>
            <span className="bar-dash-action-subtitle">{formatHours(totalHours)}</span>
          </div>
        </button>
        
        <button 
          className="bar-dash-action-btn bar-dash-menu-btn"
          onClick={() => setShowMenu(true)}
        >
          <FaUtensils className="bar-dash-action-icon" />
          <div className="bar-dash-action-text">
            <span className="bar-dash-action-title">Menu</span>
            <span className="bar-dash-action-subtitle">Consulter & Gérer</span>
          </div>
        </button>
      </div>

      {/* Section des boissons */}
      <div className="bar-dash-drinks-section">
        <div className="bar-dash-section-header">
          <h2 className="bar-dash-section-title">
            <FaGlassWhiskey className="bar-dash-section-icon" />
            Boissons à Préparer
          </h2>
          <span className="bar-dash-drinks-count">
            {filteredDrinks.length} boisson{filteredDrinks.length > 1 ? 's' : ''}
          </span>
        </div>

        {/* Barre de recherche */}
        <div className="bar-dash-search-container">
          <FaSearch className="bar-dash-search-icon" />
          <input
            type="text"
            placeholder="Rechercher par nom, table ou client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bar-dash-search-input"
          />
          {searchTerm && (
            <button 
              className="bar-dash-search-clear"
              onClick={() => setSearchTerm('')}
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Liste des boissons */}
        {loadingDrinks ? (
          <div className="bar-dash-drinks-loading">
            <FaSpinner className="bar-dash-drinks-spinner" />
            <p>Chargement des boissons...</p>
          </div>
        ) : filteredDrinks.length > 0 ? (
          <div className="bar-dash-drinks-grid">
            {filteredDrinks.map((item, index) => {
              const itemKey = `${item.commandeId}-${index}`;
              const isExpanded = expandedCards[itemKey];
              const isPrepared = item.drink_statut === 'prepared_drink';
              
              return (
                <div 
                  key={itemKey} 
                  className={`bar-dash-drink-card ${isPrepared ? 'bar-dash-drink-prepared' : ''}`}
                >
                  <div className="bar-dash-drink-header">
                    <div className="bar-dash-drink-info">
                      <FaGlassWhiskey className="bar-dash-drink-icon" />
                      <div className="bar-dash-drink-details">
                        <h3 className="bar-dash-drink-name">{item.nom}</h3>
                        <span className="bar-dash-drink-quantity">
                          x{item.quantity}
                        </span>
                      </div>
                    </div>
                    <div className={`bar-dash-drink-status ${isPrepared ? 'bar-dash-status-prepared' : 'bar-dash-status-pending'}`}>
                      {isPrepared ? <FaCheckCircle /> : <FaTimesCircle />}
                      <span>{isPrepared ? 'Préparée' : 'En attente'}</span>
                    </div>
                  </div>

                  <div className="bar-dash-drink-meta">
                    <div className="bar-dash-drink-table">
                      <span className="bar-dash-meta-label">Table</span>
                      <span className="bar-dash-meta-value">#{item.table}</span>
                    </div>
                    <div className="bar-dash-drink-client">
                      <span className="bar-dash-meta-label">Client</span>
                      <span className="bar-dash-meta-value">{item.prenom}</span>
                    </div>
                  </div>

                  {isExpanded && item.email && (
                    <div className="bar-dash-drink-expanded">
                      <p className="bar-dash-drink-email">📧 {item.email}</p>
                    </div>
                  )}

                  <div className="bar-dash-drink-actions">
                    <button
                      onClick={() => toggleCardExpansion(itemKey)}
                      className="bar-dash-expand-btn"
                    >
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    
                    {isPrepared ? (
                      <button
                        className="bar-dash-action-prepare bar-dash-action-unprepare"
                        onClick={() => toggleDrinkStatus(item.clientId, item.commandeId, item.nom, 'not_prepared')}
                      >
                        <FaTimesCircle />
                        <span>Annuler</span>
                      </button>
                    ) : (
                      <button
                        className="bar-dash-action-prepare"
                        onClick={() => toggleDrinkStatus(item.clientId, item.commandeId, item.nom, 'prepared_drink')}
                      >
                        <FaCheckCircle />
                        <span>Marquer préparée</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bar-dash-no-drinks">
            <FaGlassWhiskey className="bar-dash-no-drinks-icon" />
            <h3>Aucune boisson à préparer</h3>
            <p>{searchTerm ? 'Aucun résultat pour votre recherche' : 'Toutes les boissons sont préparées !'}</p>
          </div>
        )}
      </div>
      {/* Popup Emploi du temps */}
      {showSchedule && (
        <div className="bar-dash-popup-overlay" onClick={() => setShowSchedule(false)}>
          <div className="bar-dash-popup" onClick={(e) => e.stopPropagation()}>
            <div className="bar-dash-popup-header">
              <div className="bar-dash-popup-title-section">
                <FaCalendarAlt className="bar-dash-popup-icon" />
                <h2>Emploi du temps</h2>
              </div>
              <button 
                className="bar-dash-popup-close" 
                onClick={() => setShowSchedule(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="bar-dash-popup-content">
              <div className="bar-dash-schedule-table-wrapper">
                {renderSchedule()}
              </div>

              <div className="bar-dash-hours-summary">
                <FaClock className="bar-dash-hours-icon" />
                <div className="bar-dash-hours-details">
                  <span className="bar-dash-hours-label">Total ce mois</span>
                  <span className="bar-dash-hours-value">{formatHours(totalHours)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popup Menu */}
      {showMenu && (
        <div className="bar-dash-popup-overlay" onClick={() => setShowMenu(false)}>
          <div className="bar-dash-popup bar-dash-menu-popup" onClick={(e) => e.stopPropagation()}>
            <div className="bar-dash-popup-header">
              <div className="bar-dash-popup-title-section">
                <FaUtensils className="bar-dash-popup-icon" />
                <h2>Menu du Restaurant</h2>
              </div>
              <button 
                className="bar-dash-popup-close" 
                onClick={() => setShowMenu(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="bar-dash-popup-content bar-dash-menu-content">
              <ViewMenuServer menuId={menuId} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BarInterface;